import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Trash2, Download, Plus, Clock, FileText, Calendar } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import jsPDF from 'jspdf';

interface Task {
  id: string;
  patientName: string;
  startTime?: string;
  endTime?: string;
  taskType: string;
  customTask: string;
  description: string;
}

const Index = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [patientName, setPatientName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [taskType, setTaskType] = useState('');
  const [customTask, setCustomTask] = useState('');
  const [description, setDescription] = useState('');
  const { toast } = useToast();

  const predefinedTasks = [
    'Abführmanagement',
    'Aktivierung',
    'Allergiekontrolle',
    'An-/Ausziehen unterstützen',
    'Atembeobachtung',
    'Atemtherapie nach Anleitung',
    'Ausscheidungsmanagement',
    'Autonomie fördern',
    'Basale Stimulation',
    'Beatmungskontrolle (nichtinvasiv, falls vorhanden)',
    'Bilanzierung (Flüssigkeit)',
    'Blutdruckkontrolle',
    'Blutzuckermessung',
    'Bodycheck (oberflächliche Hautkontrolle)',
    'Braden-Screening',
    'Brechampulle / Urinportionieren',
    'CRP-/Laborwerte prüfen (in Arztauftrag)',
    'Chronische Schmerzen beobachten',
    'Check Vitalparameter',
    'Check Medikation (Richtige Zeit/Richtige Dosis)',
    'Clexane-/Thromboseprophylaxe (nicht verabreichen, nur erinnern/beobachten)',
    'Dekubitusprophylaxe',
    'Drainagen beobachten',
    'Dokumentation KIS',
    'Durst-/Essverhalten erfassen',
    'Ernährung anreichen',
    'Ernährungsstatus beobachten',
    'EZP/Essprotokoll führen',
    'Fieberkontrolle',
    'Flüssigkeitsbilanz erstellen',
    'Föderierende Gespräche (Motivation, Orientierung)',
    'Frischluftmobilisation (kurz begleiten)',
    'GCS-Überwachung',
    'Glasgow Coma Scale stündliche Kontrolle',
    'Gewichtsbestimmung',
    'Grundpflege komplett',
    'Grundpflege teilassistiert',
    'Hautbeobachtung',
    'Handhygiene schulen',
    'Hilfsmittel organisieren',
    'Hustenförderung / Atemübungen anleiten',
    'Infusionsstand prüfen (Beobachtung)',
    'Inkontinenzversorgung',
    'Intake/Output-Protokoll',
    'intravenöse Zugänge beobachten (Red Flags melden)',
    'Katheterpflege (DK/Spülung durch Pflege, wenn erlaubt)',
    'Kontinenztraining',
    'Körperpflege am Lavabo',
    'Kreislaufkontrolle',
    'Kognition beobachten',
    'Kontrakturenprophylaxe',
    'Lagerungen (30°, 90°, Mikrolagerung)',
    'Leitungen sichern (Beobachtung)',
    'Lokale Wundkontrolle',
    'Lungen-/Atemgeräusche beobachten (ohne Auskultation)',
    'Mahlzeiten richten',
    'Medikationsvorbereitung assistieren (nicht verabreichen)',
    'Mentale Orientierung fördern',
    'Mobilisation: an den Bettrand',
    'Mobilisation: in den Stand',
    'Mobilisation: ins Bad',
    'Mobilisation: mit Gehstock',
    'Mobilisation: mit Rollator',
    'Mobilisation: im Zimmer',
    'Mobilisation: auf den Korridor',
    'Mobilisation postoperativ (Neuro/Uro)',
    'Mundpflege',
    'Nausea beobachten',
    'Neurologischer Status (Pupillen, Motorik, Sensibilität)',
    'Nachtruhe unterstützen',
    'Narbenkontrolle',
    'O2-Sättigung messen',
    'Orientierungsgespräch',
    'OP-Wunde beobachten',
    'Obstipation vorbeugen',
    'Patientenedukation (Grundinfos)',
    'Positionierung nach Schema',
    'Postoperative Überwachung (Basisparameter)',
    'Pulsfrequenz messen',
    'Ressourcenförderung',
    'Respiratorische Situation beobachten',
    'Rückfragen klären',
    'Risikoassessment Sturz',
    'Schmerzassessment (NRS)',
    'Schlafprotokoll führen',
    'Sozialkontakt fördern',
    'Stomabeobachtung',
    'Sturzprophylaxe',
    'Subjektive Beschwerden erfassen',
    'Sondenbeobachtung (PEG/NG)',
    'Tagesstruktur besprechen',
    'Thromboseprophylaxe (Bewegungsförderung, Strümpfe anziehen)',
    'Toilettengang begleiten',
    'Trinkmenge motivieren',
    'Tracheostoma beobachten (falls vorhanden)',
    'Überwachungsrunde (Allgemeinzustand)',
    'Urostoma beobachten',
    'Urinfarbe und -menge beurteilen',
    'Verband beobachten',
    'Verbandsmaterial bereitstellen',
    'Vitalzeichenkontrolle',
    'Visitenbegleitung (falls vorgesehen)',
    'Wundbeobachtung',
    'Wundversorgung assistieren',
    'Wasserlassen beobachten (Miktionsprotokoll)',
    'Wohlbefinden einschätzen',
    'Zimmer aufräumen (Patientenumfeld)',
    'Zusammenarbeit mit Physio koordinieren',
    'Zustandsveränderung rapportieren'
  ];

  const validateTimeRange = (start: string, end: string): boolean => {
    if (!start || !end) return true; // Zeit ist optional
    const startMinutes = timeToMinutes(start);
    const endMinutes = timeToMinutes(end);
    return endMinutes > startMinutes;
  };

  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const addTask = () => {
    if (!patientName.trim()) {
      toast({
        title: 'Fehler',
        description: 'Bitte geben Sie einen Patientennamen ein.',
        variant: 'destructive'
      });
      return;
    }

    if (startTime && endTime && !validateTimeRange(startTime, endTime)) {
      toast({
        title: 'Fehler',
        description: 'Die Endzeit muss nach der Startzeit liegen.',
        variant: 'destructive'
      });
      return;
    }

    if (!taskType && !customTask) {
      toast({
        title: 'Fehler',
        description: 'Bitte wählen Sie eine Aufgabe aus oder geben Sie eine eigene ein.',
        variant: 'destructive'
      });
      return;
    }

    const newTask: Task = {
      id: Date.now().toString(),
      patientName: patientName.trim(),
      startTime: startTime || undefined,
      endTime: endTime || undefined,
      taskType: taskType === 'custom' ? customTask : taskType,
      customTask,
      description
    };

    setTasks(prev => {
      const updated = [...prev, newTask];
      // Sortiere nach Patient, dann nach Zeit (falls vorhanden)
      return updated.sort((a, b) => {
        const patientCompare = a.patientName.localeCompare(b.patientName);
        if (patientCompare !== 0) return patientCompare;
        
        // Wenn beide Zeiten vorhanden sind, nach Zeit sortieren
        if (a.startTime && b.startTime) {
          return timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
        }
        // Aufgaben mit Zeit vor Aufgaben ohne Zeit
        if (a.startTime && !b.startTime) return -1;
        if (!a.startTime && b.startTime) return 1;
        return 0;
      });
    });

    // Reset form
    setPatientName('');
    setStartTime('');
    setEndTime('');
    setTaskType('');
    setCustomTask('');
    setDescription('');

    toast({
      title: 'Erfolg',
      description: 'Aufgabe wurde hinzugefügt.'
    });
  };

  const deleteTask = (id: string) => {
    setTasks(prev => prev.filter(task => task.id !== id));
    toast({
      title: 'Aufgabe gelöscht',
      description: 'Die Aufgabe wurde aus dem Tagesplan entfernt.'
    });
  };

  const generatePDF = () => {
    if (tasks.length === 0) {
      toast({
        title: 'Keine Aufgaben',
        description: 'Bitte fügen Sie mindestens eine Aufgabe hinzu.',
        variant: 'destructive'
      });
      return;
    }

    const doc = new jsPDF();
    const today = new Date().toLocaleDateString('de-DE');
    
    // Header
    doc.setFontSize(20);
    doc.text('Pflegetagesplan', 20, 30);
    doc.setFontSize(12);
    doc.text(`Datum: ${today}`, 20, 45);
    
    let yPosition = 65;
    
    // Gruppiere Aufgaben nach Patient
    const tasksByPatient = tasks.reduce((acc, task) => {
      if (!acc[task.patientName]) {
        acc[task.patientName] = [];
      }
      acc[task.patientName].push(task);
      return acc;
    }, {} as Record<string, Task[]>);
    
    // Sortiere Patienten alphabetisch
    const sortedPatients = Object.keys(tasksByPatient).sort();
    
    sortedPatients.forEach((patientName, patientIndex) => {
      // Patient Header
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(`Patient: ${patientName}`, 20, yPosition);
      yPosition += 10;
      
      // Table header für diesen Patienten
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text('Zeit', 20, yPosition);
      doc.text('Aufgabe', 60, yPosition);
      doc.text('Beschreibung', 120, yPosition);
      
      // Line under header
      doc.line(20, yPosition + 2, 190, yPosition + 2);
      yPosition += 8;
      
      // Aufgaben für diesen Patienten
      tasksByPatient[patientName].forEach((task) => {
        const timeRange = task.startTime && task.endTime 
          ? `${task.startTime}-${task.endTime}` 
          : task.startTime 
            ? `ab ${task.startTime}` 
            : '-';
        const taskName = task.taskType;
        
        doc.text(timeRange, 20, yPosition);
        
        // Aufgabenname mit Zeilenumbruch falls nötig
        const taskLines = doc.splitTextToSize(taskName, 55);
        doc.text(taskLines, 60, yPosition);
        
        // Beschreibung
        if (task.description) {
          const descLines = doc.splitTextToSize(task.description, 70);
          doc.text(descLines, 120, yPosition);
          yPosition += Math.max(taskLines.length, descLines.length) * 5;
        } else {
          yPosition += taskLines.length * 5;
        }
        
        yPosition += 2; // Zusätzlicher Abstand zwischen Aufgaben
        
        // Check if we need a new page
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 30;
        }
      });
      
      // Abstand zwischen Patienten
      if (patientIndex < sortedPatients.length - 1) {
        yPosition += 10;
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 30;
        }
      }
    });
    
    doc.save(`Pflegetagesplan_${today.replace(/\./g, '_')}.pdf`);
    
    toast({
      title: 'PDF erstellt',
      description: 'Der Tagesplan wurde als PDF heruntergeladen.'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center py-6">
          <h1 className="text-3xl font-bold text-slate-800 mb-2 flex items-center justify-center gap-2">
            <Calendar className="h-8 w-8 text-blue-600" />
            Pflegeplanungstool
          </h1>
          <p className="text-slate-600">Strukturierte Tagesplanung für das Pflegepersonal</p>
        </div>

        {/* Task Input Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Neue Aufgabe hinzufügen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Patient Name */}
            <div className="space-y-2">
              <Label htmlFor="patientName" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Patientenname
              </Label>
              <Input
                id="patientName"
                value={patientName}
                onChange={(e) => setPatientName(e.target.value)}
                placeholder="Name des Patienten eingeben"
                className="w-full"
              />
            </div>

            {/* Time Range (Optional) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startTime" className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Startzeit (optional)
                </Label>
                <Input
                  id="startTime"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endTime">Endzeit (optional)</Label>
                <Input
                  id="endTime"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            {/* Task Selection */}
            <div className="space-y-2">
              <Label>Aufgabe</Label>
              <Select value={taskType} onValueChange={setTaskType}>
                <SelectTrigger>
                  <SelectValue placeholder="Aufgabe auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {predefinedTasks.map((task) => (
                    <SelectItem key={task} value={task}>
                      {task}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Eigene Aufgabe eingeben</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Custom Task Input */}
            {taskType === 'custom' && (
              <div className="space-y-2">
                <Label htmlFor="customTask">Eigene Aufgabe</Label>
                <Input
                  id="customTask"
                  value={customTask}
                  onChange={(e) => setCustomTask(e.target.value)}
                  placeholder="Aufgabenbezeichnung eingeben"
                />
              </div>
            )}

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Aufgabenbeschreibung
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Details zur Durchführung, Beobachtungen, Hinweise..."
                rows={3}
              />
            </div>

            <Button onClick={addTask} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Aufgabe hinzufügen
            </Button>
          </CardContent>
        </Card>

        {/* Task List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Tagesplan ({tasks.length} Aufgaben)</CardTitle>
            {tasks.length > 0 && (
              <Button onClick={generatePDF} variant="outline">
                <Download className="h-4 w-4 mr-2" />
                PDF herunterladen
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {tasks.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Noch keine Aufgaben geplant</p>
                <p className="text-sm">Fügen Sie Ihre erste Aufgabe hinzu</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Gruppiere Aufgaben nach Patient */}
                {Object.entries(
                  tasks.reduce((acc, task) => {
                    if (!acc[task.patientName]) {
                      acc[task.patientName] = [];
                    }
                    acc[task.patientName].push(task);
                    return acc;
                  }, {} as Record<string, Task[]>)
                )
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([patientName, patientTasks]) => (
                  <div key={patientName} className="border rounded-lg bg-white">
                    <div className="bg-slate-100 px-4 py-3 rounded-t-lg">
                      <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Patient: {patientName}
                      </h3>
                    </div>
                    <div className="p-4 space-y-3">
                      {patientTasks.map((task, taskIndex) => (
                        <div key={task.id} className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-4 mb-2">
                              {(task.startTime || task.endTime) && (
                                <span className="font-mono text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                  {task.startTime && task.endTime 
                                    ? `${task.startTime} - ${task.endTime}`
                                    : task.startTime 
                                      ? `ab ${task.startTime}`
                                      : `bis ${task.endTime}`
                                  }
                                </span>
                              )}
                              <span className="font-medium text-slate-800">
                                {task.taskType}
                              </span>
                            </div>
                            {task.description && (
                              <p className="text-slate-600 text-sm mt-2 whitespace-pre-wrap">
                                {task.description}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteTask(task.id)}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Index;
